import { Link } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import SecaoManifesto from '../components/SecaoManifesto'
import VideoExplicativo from '../components/VideoExplicativo'

export default function LandingMedico() {
  const { user, perfil } = useAuth()
  const homeRota = perfil?.role === 'medico' ? '/medico' : perfil?.role === 'paciente' ? '/app' : '/medico'

  return (
    <div
      className="min-h-screen text-white relative overflow-hidden"
      style={{
        background: `
          radial-gradient(ellipse at 20% 30%, rgba(16,185,129,0.42), transparent 55%),
          radial-gradient(ellipse at 80% 10%, rgba(110,231,183,0.15), transparent 50%),
          radial-gradient(ellipse at 70% 90%, rgba(5,150,105,0.28), transparent 55%),
          linear-gradient(135deg, #022c22 0%, #064e3b 35%, #047857 65%, #022c22 100%)
        `,
      }}
    >
      <div className="absolute inset-0 pointer-events-none opacity-30 [background-image:radial-gradient(circle_at_1px_1px,rgba(110,231,183,0.18)_1px,transparent_0)] [background-size:32px_32px]" />

      <div className="relative z-10">
        {/* HEADER */}
        <header className="px-6 py-5 max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 border border-white/25 flex items-center justify-center backdrop-blur-sm">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                <path d="M3 12h4l3-9 4 18 3-9h4" />
              </svg>
            </div>
            <div className="text-2xl font-extrabold tracking-tight">
              anamis<span className="text-emerald-300">.ai</span>
            </div>
          </Link>
          <nav className="flex items-center gap-2 sm:gap-3">
            {user ? (
              <Link to={homeRota} className="rounded-full px-5 py-2 text-sm font-semibold bg-emerald-300 text-emerald-950 hover:scale-105 transition">
                Abrir app →
              </Link>
            ) : (
              <>
                <Link to="/" className="rounded-full px-4 py-2 text-sm font-semibold text-white/85 hover:text-white transition hidden md:inline-block">
                  👤 Pacientes
                </Link>
                <Link to="/psicologos" className="rounded-full px-4 py-2 text-sm font-semibold text-white/85 hover:text-white transition hidden md:inline-block">
                  🧠 Psicólogos
                </Link>
                <Link to="/planos" className="rounded-full px-4 py-2 text-sm font-semibold text-white/85 hover:text-white transition hidden lg:inline-block">
                  Planos
                </Link>
                <Link to="/login?role=medico" className="rounded-full px-4 py-2 text-sm font-semibold text-white/85 hover:text-white transition">
                  Entrar
                </Link>
                <Link to="/cadastro?role=medico" className="rounded-full px-5 py-2 text-sm font-semibold bg-emerald-300 text-emerald-950 hover:scale-105 transition shadow-lg shadow-emerald-300/20">
                  Criar conta médico
                </Link>
              </>
            )}
          </nav>
        </header>

        {/* HERO */}
        <section className="max-w-7xl mx-auto px-6 pt-12 md:pt-24 pb-16 md:pb-24 animate-fade-up">
          <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-center">
            <div className="md:col-span-7">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-[11px] font-semibold tracking-[0.18em] uppercase text-white/85 mb-7">
                <span className="text-base">🩺</span>
                Pra médicos · Healthtech · 2026
              </span>

              <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.98] mb-7 text-balance">
                Devolva<br />
                <em className="text-gradient-shimmer not-italic">15 minutos</em><br />
                por consulta.
              </h1>

              <p className="text-lg md:text-xl text-white/85 leading-[1.7] font-light max-w-2xl mb-10 text-pretty">
                Anamis transforma os dias <strong className="text-white">entre as consultas</strong> em
                anamnese estruturada por IA — hipóteses ranqueadas, alertas urgentes, padrões
                temporais que só aparecem no dia a dia. Tudo num PDF que você lê em 30 segundos
                <strong className="text-white"> antes do seu paciente entrar na sala</strong>.
              </p>

              <div className="flex flex-wrap gap-3 mb-6">
                <Link
                  to="/cadastro?role=medico"
                  className="group inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-bold bg-emerald-300 text-emerald-950 hover:scale-105 active:scale-100 transition shadow-2xl shadow-emerald-300/25"
                >
                  Criar conta grátis
                  <span className="transition group-hover:translate-x-1">→</span>
                </Link>
                <Link
                  to="/planos"
                  className="group inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-bold bg-white/10 backdrop-blur-sm border border-white/25 hover:bg-white/20 hover:border-white/40 transition"
                >
                  Ver planos
                  <span className="transition group-hover:translate-x-1">→</span>
                </Link>
              </div>

              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/70">
                <span className="flex items-center gap-1.5">✓ Free pra começar</span>
                <span className="flex items-center gap-1.5">✓ LGPD compliant</span>
                <span className="flex items-center gap-1.5">✓ HL7/FHIR ready</span>
                <span className="flex items-center gap-1.5">✓ Cancele a qualquer hora</span>
              </div>
            </div>

            <div className="md:col-span-5 relative mt-8 md:mt-0">
              <PreviewPdf />
            </div>
          </div>
        </section>

        {/* VÍDEO EXPLICATIVO — destaque centralizado entre hero e como funciona */}
        <section className="max-w-7xl mx-auto px-6 py-12 md:py-20">
          <div className="text-center mb-8">
            <span className="text-[11px] font-semibold tracking-[0.25em] uppercase text-emerald-300">
              ▶ Em 75 segundos
            </span>
            <h2 className="font-display text-3xl md:text-4xl mt-3">
              Veja como funciona pra você
            </h2>
          </div>
          <div className="max-w-5xl mx-auto">
            <VideoExplicativo audiencia="medico" cor="#6ee7b7" />
          </div>
        </section>

        {/* COMO FUNCIONA */}
        <section className="max-w-7xl mx-auto px-6 py-16 md:py-24 border-t border-white/10">
          <div className="text-center mb-14">
            <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-emerald-300">
              Como funciona pra você
            </span>
            <h2 className="font-display text-4xl md:text-5xl leading-tight mt-3 text-balance max-w-3xl mx-auto">
              Do agendamento à consulta
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-3 md:gap-4">
            <PassoCard num="01" icone="📅" titulo="Agenda a consulta" descricao="No painel ou via convite que você manda pro paciente por WhatsApp. Ciclo de check-ins é disparado." />
            <PassoCard num="02" icone="📲" titulo="Paciente registra" descricao="2 min por dia no celular dele: humor, sintomas, mapa corporal de dor, sono, medicamentos." />
            <PassoCard num="03" icone="🪄" titulo="Você gera a anamnese" descricao="No dia da consulta, 1 clique → IA processa todos os check-ins → PDF estruturado em segundos." />
            <PassoCard num="04" icone="🩺" titulo="Consulta produtiva" descricao="Lê o PDF em 30s, vai direto pro que importa. Hipóteses + CID-10 + alertas urgentes em destaque." />
          </div>
        </section>

        {/* IA COMO ALIADA — destaque pra insights só visíveis em check-ins diários */}
        <section className="max-w-7xl mx-auto px-6 py-16 md:py-24 border-t border-white/10">
          <div className="text-center mb-14">
            <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-emerald-300">
              IA como aliada clínica
            </span>
            <h2 className="font-display text-4xl md:text-5xl leading-tight mt-3 text-balance max-w-3xl mx-auto">
              Padrões que só aparecem entre as consultas
            </h2>
            <p className="text-white/75 mt-4 max-w-2xl mx-auto text-pretty">
              Sintomas que somem antes de você ver. Dor que muda de lugar ao longo da semana.
              Sono que piora antes do humor cair. Medicação que para de fazer efeito num dia
              específico. <strong className="text-white">Tudo invisível na consulta de 15 minutos
              — visível com check-in diário.</strong>
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5 max-w-5xl mx-auto">
            <CardInsightIA
              emoji="📈"
              titulo="Padrão temporal de sintomas"
              exemplo="Cefaleia que piora 2 dias antes da menstruação · Dor lombar que segue gatilhos posturais · Tontura que acompanha picos de pressão"
              valor="A IA correlaciona temporalmente — não vê só o snapshot do dia da consulta."
            />
            <CardInsightIA
              emoji="🧠"
              titulo="Hipóteses ranqueadas com CID-10"
              exemplo="3 hipóteses prováveis por confiança decrescente · Justificativa baseada nos check-ins · CID-10 sugerido quando confiança >70%"
              valor="Você valida ou descarta na consulta. IA é ponto de partida, não conclusão."
            />
            <CardInsightIA
              emoji="⚠️"
              titulo="Red flags em destaque"
              exemplo="Dor torácica + dispneia · Cefaleia súbita intensa · Febre alta persistente · Alterações neurológicas · Sangramento"
              valor="Banner vermelho no topo do PDF. Você vê a urgência antes mesmo do diagnóstico."
            />
            <CardInsightIA
              emoji="💬"
              titulo="Perguntas sugeridas pra consulta"
              exemplo="'Investigar histórico familiar de cefaleia' · 'Avaliar adesão à medicação X' · 'Considerar exame complementar Y'"
              valor="IA sugere o que perguntar pra fechar diagnóstico. Você decide."
            />
          </div>
        </section>

        {/* BENEFICIOS — expandido */}
        <section className="max-w-7xl mx-auto px-6 py-16 md:py-24 border-t border-white/10">
          <div className="text-center mb-14">
            <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-emerald-300">
              O que muda na sua prática
            </span>
            <h2 className="font-display text-4xl md:text-5xl leading-tight mt-3">
              Mais consultas. Mais tempo. Menos atrito.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            <Beneficio emoji="⏱" titulo="-15 min por consulta" descricao="Anamnese pronta antes do paciente entrar. Você usa o tempo no que só você sabe fazer: examinar, diagnosticar, escutar." />
            <Beneficio emoji="📈" titulo="Histórico longitudinal" descricao="Linha do tempo entre todas as consultas. Você acompanha evolução — não só fotografia do dia." />
            <Beneficio emoji="💬" titulo="Canal direto com paciente" descricao="Chat seguro paciente↔médico no app. Read receipts, anexar imagens, mensagem fixada. Tudo registrado e auditável." />
            <Beneficio emoji="🔒" titulo="Segurança LGPD" descricao="Dados criptografados em trânsito (HTTPS) e em repouso (GCP São Paulo). Acesso por papel — paciente, médico vinculado, ninguém mais. Direito ao esquecimento (Art. 18 LGPD) embutido." />
            <Beneficio emoji="📋" titulo="Dados estruturados FHIR/HL7" descricao="Padrão internacional. Exporta tudo em 1 clique pra qualquer prontuário (iClinic, Tasy, MV, Memed). Compatível com RNDS/SUS." />
            <Beneficio emoji="🪄" titulo="PDF pré-anamnese 1-clique" descricao="Botão único gera o PDF estruturado: dados do paciente, motivo, alertas urgentes em vermelho, hipóteses, perguntas sugeridas, timeline." />
            <Beneficio emoji="🔔" titulo="Notificação de riscos" descricao="Push imediato quando paciente sinaliza algo crítico. Você não espera próxima consulta pra agir." />
            <Beneficio emoji="✨" titulo="Branding da sua clínica" descricao="Plano PRO: paciente vê seu logo + cor da clínica no app. Identidade da sua marca, não da Anamis." />
            <Beneficio emoji="📊" titulo="Analytics agregado" descricao="Dashboard com métricas dos seus pacientes: alertas urgentes, top sintomas reportados, % com check-in hoje/semana." />
          </div>
        </section>

        {/* DINÂMICA CLÍNICA — como Anamis muda sua rotina */}
        <section className="max-w-7xl mx-auto px-6 py-16 md:py-24 border-t border-white/10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-emerald-300">
                Rotina clínica reorganizada
              </span>
              <h2 className="font-display text-3xl md:text-4xl leading-tight mt-3 mb-5">
                A consulta vira o que ela deveria ser
              </h2>
              <div className="space-y-4 text-white/85 leading-relaxed">
                <p>
                  Hoje, você gasta os primeiros 5 minutos preenchendo formulário,
                  outros 5 ouvindo o paciente reconstruir a semana de memória, e sobram 5 minutos
                  pra exame físico, decisão e explicação.
                </p>
                <p>
                  Com Anamis, esses 10 minutos viram <strong className="text-white">decisão clínica</strong>.
                  Você chega lendo o PDF, valida com perguntas-alvo, examina, decide.
                  O paciente sai entendendo o que tem e o que fazer.
                </p>
                <p className="text-emerald-300 font-semibold">
                  Mais consultas por dia, sem perder qualidade. Pelo contrário — qualidade aumenta.
                </p>
              </div>
            </div>
            <div className="rounded-3xl bg-white/8 backdrop-blur-sm border border-white/15 p-8">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-300 mb-4">
                Em uma consulta de 15 min
              </div>
              <ul className="space-y-3">
                <ComparativoLinha tempo="0:00" sem="📝 Preenche formulário" com="📖 Lê PDF pré-anamnese" />
                <ComparativoLinha tempo="2:30" sem="❓ Pergunta o básico" com="🎯 Pergunta-alvo" />
                <ComparativoLinha tempo="5:00" sem="🤔 Tenta lembrar histórico" com="🩺 Examina o paciente" />
                <ComparativoLinha tempo="7:30" sem="🩺 Examina rápido" com="💡 Decisão clínica" />
                <ComparativoLinha tempo="10:00" sem="💊 Decide rápido" com="🗣 Explica calma" />
                <ComparativoLinha tempo="12:30" sem="✍️ Escreve receita" com="✍️ Receita + plano" />
                <ComparativoLinha tempo="15:00" sem="🚪 Próximo" com="🚪 Próximo" />
              </ul>
              <div className="mt-5 pt-4 border-t border-white/10 text-xs text-white/60 leading-relaxed">
                <strong className="text-white">Sem Anamis</strong>: 5min preenchendo, 5min anamnese,
                5min restante pra tudo.<br/>
                <strong className="text-emerald-300">Com Anamis</strong>: 30s lendo PDF, 14:30
                pra cuidar do paciente.
              </div>
            </div>
          </div>
        </section>

        {/* SEGURANÇA + COMPLIANCE */}
        <section className="max-w-7xl mx-auto px-6 py-16 md:py-24 border-t border-white/10">
          <div className="text-center mb-14">
            <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-emerald-300">
              Segurança e responsabilidade
            </span>
            <h2 className="font-display text-4xl md:text-5xl leading-tight mt-3">
              Construído pra ambiente médico
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-4 max-w-6xl mx-auto">
            <CardCompliance
              emoji="🔒"
              titulo="LGPD compliant"
              texto="Dados criptografados, acesso por papel, direito ao esquecimento (Art. 18) embutido. Auditoria de acesso registrada."
            />
            <CardCompliance
              emoji="🇧🇷"
              titulo="Dados no Brasil"
              texto="Hospedagem em São Paulo (GCP southamerica-east1). Não saem do território nacional. Soberania de dados garantida."
            />
            <CardCompliance
              emoji="🤝"
              titulo="IA não treina com seus dados"
              texto="Claude (Anthropic) processa via API com política contratual que proíbe uso pra treino de modelos. Seus pacientes ficam só seus."
            />
            <CardCompliance
              emoji="📜"
              titulo="Conformidade CFM"
              texto="Estrutura compatível com CFM Resolução 2299/2021 (prontuário eletrônico) e RNDS (Rede Nacional de Dados em Saúde, SUS)."
            />
          </div>
        </section>

        {/* PLANOS */}
        <section className="max-w-7xl mx-auto px-6 py-16 md:py-24 border-t border-white/10">
          <div className="text-center mb-12">
            <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-emerald-300">
              Planos pra médicos
            </span>
            <h2 className="font-display text-4xl md:text-5xl leading-tight mt-3">
              Comece grátis, escale conforme cresce
            </h2>
            <p className="text-white/75 mt-4 max-w-2xl mx-auto">
              Pacientes sempre grátis. Você paga só pelo que usa. Cancele a qualquer momento.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 max-w-5xl mx-auto mb-10">
            <PlanoCard
              nome="Free"
              preco="R$ 0"
              descricao="Pra conhecer"
              destaques={['Até 5 pacientes', 'Análise IA básica', 'Chat + linha do tempo', 'Suporte por comunidade']}
            />
            <PlanoCard
              nome="Essencial"
              preco="R$ 79"
              periodo="/mês"
              descricao="Pra clínica individual"
              destaques={['Até 50 pacientes', 'IA completa + hipóteses + alertas', 'Agenda visual + countdown', 'Templates de resposta', 'Export FHIR']}
              destaque
            />
            <PlanoCard
              nome="PRO"
              preco="R$ 199"
              periodo="/mês"
              descricao="Pra clínica multi-médico"
              destaques={['Pacientes ilimitados', 'Branding da clínica', 'Até 5 médicos por clínica', 'Multi-clínicas', 'Suporte prioritário']}
            />
          </div>

          <div className="text-center">
            <Link
              to="/planos"
              className="group inline-flex items-center gap-2 rounded-full px-8 py-4 font-bold bg-emerald-300 text-emerald-950 hover:scale-105 transition shadow-2xl shadow-emerald-300/25"
            >
              Ver detalhes & assinar
              <span className="transition group-hover:translate-x-1">→</span>
            </Link>
          </div>
        </section>

        {/* MANIFESTO — pitch core adaptado pra médico */}
        <SecaoManifesto audiencia="medico" />

        {/* CTA FINAL */}
        <section className="max-w-7xl mx-auto px-6 py-16 md:py-24 border-t border-white/10 text-center">
          <h2 className="font-display text-4xl md:text-5xl mb-4">Pronto pra economizar tempo?</h2>
          <p className="text-white/80 max-w-xl mx-auto mb-8">
            Crie sua conta grátis em 1 minuto. Convide 1 paciente, faça uma análise. Sem cartão, sem compromisso.
          </p>
          <Link
            to="/cadastro?role=medico"
            className="inline-flex items-center gap-2 rounded-full px-8 py-4 font-bold bg-emerald-300 text-emerald-950 hover:scale-105 transition shadow-2xl shadow-emerald-300/25"
          >
            Criar conta grátis →
          </Link>
        </section>

        {/* FOOTER */}
        <footer className="border-t border-white/10 mt-12">
          <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-white/15 border border-white/25 flex items-center justify-center">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                  <path d="M3 12h4l3-9 4 18 3-9h4" />
                </svg>
              </div>
              <div className="text-lg font-extrabold">
                anamis<span className="text-emerald-300">.ai</span>
              </div>
            </div>
            <div className="text-xs text-white/55 tracking-wider text-center md:text-right">
              © 2026 Anamis · São Paulo · LGPD compliant · suporte@pluma.tv
            </div>
          </div>
        </footer>
      </div>
    </div>
  )
}

function PassoCard({ num, icone, titulo, descricao }: { num: string; icone: string; titulo: string; descricao: string }) {
  return (
    <div className="rounded-3xl bg-white/8 backdrop-blur-sm border border-white/15 p-6 hover:bg-white/12 transition">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center font-display text-xl text-emerald-300 bg-emerald-300/15 border border-emerald-300/30">
          {num}
        </div>
        <span className="text-2xl">{icone}</span>
      </div>
      <h3 className="font-bold text-lg mb-2">{titulo}</h3>
      <p className="text-sm text-white/75 leading-relaxed">{descricao}</p>
    </div>
  )
}

function CardInsightIA({ emoji, titulo, exemplo, valor }: { emoji: string; titulo: string; exemplo: string; valor: string }) {
  return (
    <div className="rounded-3xl bg-white/8 backdrop-blur-sm border border-white/15 p-7 hover:bg-white/12 transition">
      <div className="flex items-start gap-3 mb-3">
        <span className="text-3xl flex-shrink-0">{emoji}</span>
        <h3 className="font-display text-xl font-bold text-white pt-1">{titulo}</h3>
      </div>
      <div className="rounded-xl bg-emerald-950/50 border border-white/10 p-3 mb-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-300 mb-1">Exemplos</div>
        <p className="text-xs text-white/85 leading-relaxed">{exemplo}</p>
      </div>
      <p className="text-sm text-white/75 leading-relaxed">
        <span className="text-emerald-300 font-semibold">→</span> {valor}
      </p>
    </div>
  )
}

function ComparativoLinha({ tempo, sem, com }: { tempo: string; sem: string; com: string }) {
  return (
    <li className="flex items-start gap-3 text-sm">
      <span className="text-[11px] font-bold text-white/50 min-w-[40px] pt-0.5">{tempo}</span>
      <div className="flex-1 grid grid-cols-2 gap-2 text-xs">
        <span className="text-white/60 line-through decoration-red-400/60">{sem}</span>
        <span className="text-emerald-300 font-semibold">{com}</span>
      </div>
    </li>
  )
}

function CardCompliance({ emoji, titulo, texto }: { emoji: string; titulo: string; texto: string }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-6 hover:bg-white/8 transition">
      <div className="text-3xl mb-3">{emoji}</div>
      <h3 className="font-bold text-base mb-2">{titulo}</h3>
      <p className="text-xs text-white/70 leading-relaxed">{texto}</p>
    </div>
  )
}

function Beneficio({ emoji, titulo, descricao }: { emoji: string; titulo: string; descricao: string }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-6 hover:bg-white/8 transition">
      <div className="text-3xl mb-3">{emoji}</div>
      <h3 className="font-bold text-lg mb-2">{titulo}</h3>
      <p className="text-sm text-white/75 leading-relaxed" dangerouslySetInnerHTML={{ __html: descricao }} />
    </div>
  )
}

function PlanoCard({ nome, preco, periodo, descricao, destaques, destaque }: { nome: string; preco: string; periodo?: string; descricao: string; destaques: string[]; destaque?: boolean }) {
  return (
    <div className={`rounded-3xl p-7 transition relative ${destaque ? 'bg-emerald-300 text-emerald-950 shadow-2xl shadow-emerald-300/20 scale-105' : 'bg-white/8 backdrop-blur-sm border border-white/15 text-white hover:bg-white/12'}`}>
      {destaque && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-950 text-emerald-300 text-[10px] font-bold tracking-wider uppercase px-3 py-1 rounded-full">
          Mais popular
        </span>
      )}
      <h3 className="font-display text-2xl font-bold mb-1">{nome}</h3>
      <p className={`text-sm mb-4 ${destaque ? 'text-emerald-950/70' : 'text-white/70'}`}>{descricao}</p>
      <div className="mb-5">
        <span className="text-3xl font-extrabold">{preco}</span>
        {periodo && <span className={`text-sm ${destaque ? 'text-emerald-950/70' : 'text-white/70'}`}>{periodo}</span>}
      </div>
      <ul className="space-y-1.5 text-sm">
        {destaques.map((d, i) => (
          <li key={i} className="flex gap-2">
            <span className={destaque ? 'text-emerald-950' : 'text-emerald-300'}>✓</span>
            <span>{d}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function PreviewPdf() {
  return (
    <div className="relative mx-auto" style={{ maxWidth: 380 }}>
      <div className="absolute inset-0 -m-8 rounded-3xl bg-emerald-300/15 blur-3xl animate-pulse-glow" />
      <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden border-4 border-emerald-900/50 animate-float">
        {/* "Header" do PDF — gradient roxo escuro */}
        <div className="bg-emerald-950 px-5 py-4 text-white">
          <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider opacity-80 mb-1">
            <span>anamis.ai</span>
            <span>Pré-anamnese clínica</span>
          </div>
          <div className="font-display text-xl">Pré-anamnese: Maria Silva</div>
        </div>

        {/* Tabela de dados */}
        <div className="px-5 py-3 text-[11px] text-slate-700 border-b border-slate-200 grid grid-cols-2 gap-1">
          <div><strong>Paciente:</strong> Maria Silva</div>
          <div><strong>CPF:</strong> 123.456.789-00</div>
          <div><strong>Sexo:</strong> Feminino</div>
          <div><strong>Idade:</strong> 42</div>
          <div><strong>Médico:</strong> Dr. Bruno</div>
          <div><strong>CRM:</strong> 12345/SP</div>
        </div>

        {/* Alerta urgente */}
        <div className="mx-5 my-3 px-3 py-2 rounded-lg bg-red-50 border border-red-200">
          <div className="text-[9px] font-bold uppercase tracking-wider text-red-800">⚠ Alerta urgente</div>
          <div className="text-[11px] text-red-900 mt-0.5">Cefaleia persistente + tontura há 5 dias</div>
        </div>

        {/* Resumo clínico */}
        <div className="px-5 mb-3">
          <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">Resumo clínico</div>
          <p className="text-[11px] text-slate-800 leading-snug">
            Paciente refere cefaleia frontal de moderada intensidade há 5 dias, associada a episódios de tontura...
          </p>
        </div>

        {/* Hipóteses */}
        <div className="px-5 pb-4">
          <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-2">Hipóteses ranqueadas</div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <div className="text-[10px] font-bold text-anamis-700 min-w-[28px]">87%</div>
              <div className="h-1.5 flex-1 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-anamis-600" style={{ width: '87%' }} />
              </div>
              <div className="text-[10px] text-slate-700 min-w-[100px]">Cefaleia tensional</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-[10px] font-bold text-anamis-700 min-w-[28px]">62%</div>
              <div className="h-1.5 flex-1 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-anamis-600" style={{ width: '62%' }} />
              </div>
              <div className="text-[10px] text-slate-700 min-w-[100px]">Hipertensão</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
